import Cookies from 'js-cookie';
import moment from 'moment/moment';
import Handlebars from 'handlebars';

import { FondsDetailZusammensetzung } from './FondsDetailZusammensetzung';
import { FondsDetailChart } from './FondsDetailChart';
import { FondsDetailConverters } from './FondsDetailConverters';

import AMApp from '../../../../../../../src/global/app';
import TogglePane from '../../../../../../../src/global/TogglePane';


export class FondsDetail {
  constructor(element, options = {}) {
    console.log('FondsDetail.constructor()');
    this.element = element;
    this.amApp = new AMApp();

    this.settings = Object.assign({
      routeFondsdetail: AMApp.langGetParam + 'eID=fonds&function=fondsdetail&isin=',
      routeZusammensetzung: AMApp.langGetParam + 'eID=fonds&function=zusammensetzung&fondsId=',
      routeKaufen: '?eID=fonds&function=kaufen&fondsId=',
      fileadminPath: '/fileadmin/',
      routeConfig: AMApp.langGetParam + 'eID=fonds&function=getConfig&isin='
    }, options);

    // Init variables
    this.fondsdetailsTarget = document.getElementById('fondsdetails');
    this.initialDateRange = '1y';
    this.selectedDateRange = null;
    this.urlParams = new URLSearchParams(window.location.search);
    this.isin = this.getIsin();
    this.fondsCategory = null;
    this.currentTime = new Date();
    this.chartName = 'wertentwicklung';

    // State variables
    this.disclaimerAccepted = Cookies.get('LBBWAMDisclaimerAccepted');
    this.letzteBewertungDate = null;
    this.fondsData = null;
    this.isFondsYoungerThanOneYear = false;

    // Templates
    this.resultRaw = document.getElementById('fondsdetails')?.innerHTML || '';
    this.resultTemplate = Handlebars.compile(this.amApp.toHandlebarsDelimiters(this.resultRaw));
    this.buyFondsTarget = document.getElementById('buy_fonds_target');
    this.buyFondsRaw = document.getElementById('buy_fonds_template')?.innerHTML || '';
    this.buyFondsTemplate = Handlebars.compile(this.amApp.toHandlebarsDelimiters(this.buyFondsRaw));

    this.fondsCategories = [
      'AbsoluteReturnFonds',
      'Aktienfonds',
      'Dachfonds',
      'Geldmarktfonds',
      'Mischfonds',
      'Rentenfonds',
      'Rohstofffonds',
      'Partnerfonds'
    ];

    this.fondsCategoriesOrder = {
      AbsoluteReturnFonds: { chartOrder: ['TopKapitalstruktur', 'TOPPositionen', 'TOPBranchen', 'TOPLaender'] },
      Aktienfonds: { chartOrder: ['TOPPositionen', 'TOPBranchen', 'TOPLaender'] },
      Dachfonds: { chartOrder: ['Portfoliostruktur', 'TOPPositionen'] },
      Geldmarktfonds: { chartOrder: ['TopKapitalstruktur', 'TOPPositionen', 'TOPBranchen', 'TOPLaender'] },
      Mischfonds: { chartOrder: ['Portfoliostruktur', 'TOPPositionenAktien', 'TOPBranchenAktien', 'TOPLaenderAktien', 'TOPPositionenRenten', 'TOPBranchenRenten', 'TOPLaenderRenten'] },
      Partnerfonds: { chartOrder: ['Portfoliostruktur', 'TOPPositionenAktien', 'TOPBranchenAktien', 'TOPLaenderAktien', 'TOPPositionenRenten', 'TOPBranchenRenten', 'TOPLaenderRenten'] },
      Rentenfonds: { chartOrder: ['TopKapitalstruktur', 'TOPPositionen', 'TOPBranchen', 'TOPLaender'] },
      Rohstofffonds: { chartOrder: ['RohstoffeImLBBWIndex', 'TOPAssetklassen', 'TOPPositionenCollateral', 'TOPLaenderCollateral'] },
      LBBWAktienDeutschland: { chartOrder: ['TOPPositionen', 'TOPBranchen'] },
      LBBWRohstoffeRessourcen: { chartOrder: ['TOPPositionen'] },
      BWPortfolio: { chartOrder: ['Portfoliostruktur', 'TOPPositionen'] }
    };

    this.init();

    this.zusammensetzung = new FondsDetailZusammensetzung(element, options);
    this.fondsDetailChart = new FondsDetailChart(element, options);
    this.converters = new FondsDetailConverters();
  }

  init() {
    console.log('FondsDetail.init() - isin: ', this.getIsin());
    this.fondsAndiId = "";
    this.fondsDetailData = {};
    this.config = {};
    this.IS_SAFARI_BROWSER = !!navigator.userAgent.match(/safari/i);

    this.getFondsDetailData().then(fondsDetailData => {
      console.log('FondsDetail.init() - fondsDetailData #0: ', fondsDetailData);
      this.fondsDetailData = fondsDetailData;
      this.fondsAndiId = fondsDetailData.INSD_ANDI_ID;
      this.glossData = this.setGloss(fondsDetailData);
      console.log('FondsDetail.init() - fondsAndiId: ', this.fondsAndiId);

      this.initComponents();
      this.fondsDetailDataSuccess(fondsDetailData);

    }).catch(error => {
      this.showFondsDetailStatus('error');
    }).finally(() => {
      this.removeLoader();
    });
  }

  /**
   *
   * @returns {undefined|*|string}
   */
  getIsin() {
    const pathname = window.location.pathname;

    if (/\/fonds\//.test(pathname)) {
      const fondsdetailPath = pathname.split('/');
      if (AMApp.isGerman) {
        return fondsdetailPath[2];
      }
      return fondsdetailPath[3] || fondsdetailPath[2];
    }

    if (this.urlParams.has('isin')) {
      return this.urlParams.get('isin');
    }

    if (this.urlParams.has('tx_lbbwpackage_fondsdetail[isin]')) {
      return this.urlParams.get('tx_lbbwpackage_fondsdetail[isin]');
    }

    if (this.fondsdetailsTarget && this.fondsdetailsTarget.dataset.isin) {
      return this.fondsdetailsTarget.dataset.isin.replace(/^\/+|\/+$/g, '');
    }

    return undefined;
  }

  /**
   *
   * @returns {Promise<any>}
   */
  async getFondsDetailData() {
    if (!this.fondsdetailsTarget) return;

    this.fondsdetailsTarget.classList.add('fondsdetail-content__loading');
    this.fondsdetailsTarget.insertAdjacentHTML('afterend', AMApp.LOADING_ANIMATION);

    if (this.isin) {
      try {
        const response = await fetch(`${AMApp.basePath}${this.settings.routeFondsdetail}${this.isin}`);

        this.fondsdetailsTarget.classList.remove('fondsdetail-content__loading');

        if (!response.ok) {
          this.showFondsDetailStatus('error');
          return;
        }

        return response.json();
      } catch (error) {
        this.fondsdetailsTarget.classList.remove('fondsdetail-content__loading');
        this.showFondsDetailStatus('error');
      }
    } else {
      console.error('No ISIN found.');
      this.showFondsDetailStatus('noIsin');
    }
  }

  /**
   * Init FondsDetail Components
   */
  initComponents() {
    this.fondsCategory = this.getFondsCategory(this.fondsDetailData.categories.split(','));
    this.zusammensetzung.init(this.isin, this.fondsAndiId, this.glossData, this.fondsDetailData, this.fondsCategory, this.fondsCategoriesOrder);
    this.fondsDetailChart.init(this.fondsDetailData);

    this.attachEventListeners();
  }

  /**
   * Attaches various event listeners to handle user interactions within the application.
   * These include click events, change events, and keyboard events to ensure appropriate
   * functionality of UI components such as opening/closing layers, toggling charts, updating charts,
   * handling redirects, managing radio button behaviors, and responding to keyboard inputs.
   *
   * @return {void} This method does not return a value.
   */
  attachEventListeners() {
    // Buy buttons
    document.addEventListener('click', (e) => {
      if (e.target.closest('.buy-fonds-open-btn')) {
        this.openBuyLayer();
      }

      if (e.target.closest('#buy_fonds_close')) {
        this.closeBuyLayer();
      }

      if (e.target.name === 'fondsdetailchart') {
        AMApp.chartName = e.target.value;
        if (AMApp.chartName === 'wertentwicklung') {
          this.showChartWertentwicklung();
        }
        if (AMApp.chartName === 'preisentwicklung') {
          this.showChartPreisentwicklung();
        }
      }

      if (e.target.id === 'chart_range_update') {
        e.preventDefault();
        setTimeout(() => {
          this.amApp.getChartDataBasedOnDatepicker();
        }, 250);
      }

      const buyNext = e.target.closest('#buy_to_step2');
      if (buyNext) {
        e.preventDefault();
        if (this.disclaimerAccepted) {
          window.open(buyNext.href, '_blank');
        } else {
          this.showBuyStep2();
        }
      }

      const notesToggle = e.target.closest('.fondsdetail-hint-notes-toggle');
      if (notesToggle) {
        e.preventDefault();
        this.toggleHintNotes(notesToggle);
      }

      const buyRedirect = e.target.closest('#buy_redirect');
      if (buyRedirect) {
        e.preventDefault();
        this.handleBuyRedirect(buyRedirect);
      }

      if (e.target.classList.contains('js_print_chart')) {
        this.printChart(e.target);
      }

      if (e.target.classList.contains('js_chartcontrol')) {
        this.chartName = e.target.value;
        this.handleChartToggle(e.target);
      }
    });

    // Change events
    document.addEventListener('change', (e) => {
      if (e.target.type === 'radio' && this.buyFondsTarget && this.buyFondsTarget.contains(e.target)) {
        this.handleRadioChange(e.target);
      }
    });

    // Keyboard events
    document.addEventListener('keyup', (e) => {
      if ((e.key === 'Enter' || e.key === ' ') && e.target.classList.contains('js_chartcontrol')) {
        AMApp.chartName = e.target.value;
        this.handleChartToggle(e.target);
      }
    });

    document.addEventListener('keydown', (e) => {
      if (e.key === ' ' || e.key === 'Enter') {
        const input = e.target.querySelector('input');
        if (input) {
          const value = input.value;
          input.checked = true;

          if (value === 'wertentwicklung') {
            const preisentwicklung = document.getElementById('preisentwicklung');
            if (preisentwicklung) preisentwicklung.checked = false;
            this.showChartWertentwicklung();
          }
          if (value === 'preisentwicklung') {
            const wertentwicklung = document.getElementById('wertentwicklung');
            if (wertentwicklung) wertentwicklung.checked = false;
            this.showChartPreisentwicklung();
          }
        }
      }
    });
  }

  /**
   * Open buy layer
   */
  openBuyLayer() {
    const buyLayer = document.getElementById('fondsdetail_buy');
    const body = document.body;

    if (buyLayer) buyLayer.classList.add('fondsdetail-buy__open');
    if (body) body.classList.add('openlayer');
  }

  /**
   * Close buy layer
   */
  closeBuyLayer() {
    const buyLayer = document.getElementById('fondsdetail_buy');
    const buyStep1 = document.getElementById('buy_step1');
    const buyStep2 = document.getElementById('buy_step2');
    const body = document.body;

    if (buyLayer) buyLayer.classList.remove('fondsdetail-buy__open');
    if (buyStep1) buyStep1.classList.remove('fondsdetail-buy-step__hidden');
    if (buyStep2) buyStep2.classList.add('fondsdetail-buy-step__hidden');
    if (body) body.classList.remove('openlayer');
  }

  /**
   * Show buy step 2
   */
  showBuyStep2() {
    const buyStep1 = document.getElementById('buy_step1');
    const buyStep2 = document.getElementById('buy_step2');

    if (buyStep1) buyStep1.classList.add('fondsdetail-buy-step__hidden');
    if (buyStep2) buyStep2.classList.remove('fondsdetail-buy-step__hidden');
  }

  /**
   * Toggle hint notes
   */
  toggleHintNotes(button) {
    const isActive = !button.classList.contains('active');
    const controlsId = button.getAttribute('aria-controls');
    const content = controlsId ?
      document.getElementById(controlsId) :
      document.querySelector('.fondsdetail-hint-notes-content');

    button.classList.toggle('active', isActive);
    button.setAttribute('aria-expanded', String(isActive));

    if (content) {
      if (isActive) {
        content.removeAttribute('hidden');
        content.classList.add('toggle__active');
        content.setAttribute('tabindex', '-1');
        content.focus();
      } else {
        content.setAttribute('hidden', 'hidden');
        content.classList.remove('toggle__active');
      }
    }
  }

  /**
   * Handle radio change
   */
  handleRadioChange(radio) {
    const value = radio.value;
    const buyToStep2 = document.getElementById('buy_to_step2');
    const buyRedirect = document.getElementById('buy_redirect');
    const buyStep1Footer = document.getElementById('buy_step1_footer');

    if (this.disclaimerAccepted && buyToStep2) {
      buyToStep2.href = value;
    }

    if (buyRedirect) buyRedirect.href = value;
    if (buyStep1Footer) buyStep1Footer.classList.remove('fondsdetail-buy-footer__hidden');
  }

  /**
   * Handle buy redirect
   */
  handleBuyRedirect(button) {
    const disclaimerCheckbox = document.getElementById('accept_disclaimer_buy')
      || document.getElementById('accept_disclaimer_buyfonds');

    if (disclaimerCheckbox && disclaimerCheckbox.checked) {
      Cookies.set('LBBWAMDisclaimerAccepted', 'true', { expires: 3650, path: '/' });
    }

    window.open(button.href, '_blank');
  }

  /**
   * Print chart
   */
  printChart(button) {
    const printArea = button.closest('.js_print_area');
    if (printArea && window.print) {
      // Simple print implementation - you might want to use a proper print library
      const originalContent = document.body.innerHTML;
      document.body.innerHTML = printArea.innerHTML;
      window.print();
      document.body.innerHTML = originalContent;
    }
  }

  /**
   * Handle chart toggle
   */
  handleChartToggle(target) {
    document.querySelectorAll('.js_chartcontrol').forEach(control => {
      control.classList.remove('active');
    });
    target.classList.add('active');

    if (this.chartName === 'wertentwicklung') {
      this.showChartWertentwicklung();
    }
    if (this.chartName === 'preisentwicklung') {
      this.showChartPreisentwicklung();
    }
  }


  /**
   * Handle successful data fetch
   */
  fondsDetailDataSuccess() {
    this.setBasicVariables();
    this.renderFondsDetailPage();
  }

  showFondsDetailStatus(status) {
    this.removeLoader();

    const messages = {
      error: AMApp.translations?.common?.noService || 'Service nicht verfügbar',
      noIsin: AMApp.translations?.common?.noIsin || 'Keine ISIN gefunden'
    };

    if (messages[status]) {
      this.showMessage(this.fondsdetailsTarget, messages[status]);
    }
  }

  setBasicVariables() {
    this.fondsCategory = this.getFondsCategory(this.fondsDetailData.categories.split(','));
    this.letzteBewertungDate = this.fondsDetailData.INSD_LETZTE_BEWERTUNG;
  }


  renderFondsDetailPage(data) {
    if (!this.fondsDetailData) {
      this.showMessage(this.fondsdetailsTarget, AMApp.translations?.fondsdetail?.noResultMessage || 'Keine Ergebnisse');
      this.removeLoader();
      return;
    }

    // Process chancen and risiken
    if (this.fondsDetailData.INSD_CHANCEN) {
      const chancen = this.fondsDetailData.INSD_CHANCEN.replace('$++$ ', '').split('$++$');
      this.fondsDetailData.INSD_CHANCEN = this.converters.createList(chancen);
    }

    if (this.fondsDetailData.INSD_RISIKEN) {
      const risiken = this.fondsDetailData.INSD_RISIKEN.replace('$--$ ', '').split('$--$');
      this.fondsDetailData.INSD_RISIKEN = this.converters.createList(risiken);
    }

    this.fondsdetailsTarget.classList.remove('fondsdetail-content__loading');
    // API data can arrive after the user has already selected or closed a panel.
    const toggleStates = TogglePane.captureState(this.fondsdetailsTarget);
    this.fondsdetailsTarget.innerHTML = this.resultTemplate(data);
    TogglePane.restoreState(this.fondsdetailsTarget, toggleStates);

    if (window.innerWidth < AMApp.CONSTANTS.BREAKPOINT.XL) {
      // Tablesaw.init();
    }

    this.setDateRange();
    this.checkFondsManagerImageAvailable();
    this.removeEmptyTableCells();
    this.getChartData().then((chartData) => {
      this.fondsDetailChart.renderFondsDetailChartWertentwicklung(chartData)
      this.fondsDetailChart.renderFondsDetailChartPreisentwicklung(chartData);
      this.renderIcmaLegend(chartData);

      if (this.IS_SAFARI_BROWSER) {
        this.showChartPreisentwicklung();
        setTimeout(() => {
          this.showChartWertentwicklung();
        }, 250);
      } else {
        this.showChartWertentwicklung();
      }
    });
  }


  /********************************************************************
   * Wertentwicklung & Preisentwicklung
   ********************************************************************/
  showChart(chartName) {
    const wertentwicklung = document.getElementById('fondsdetail_chart_wertentwicklung');
    const preisentwicklung = document.getElementById('fondsdetail_chart_preisentwicklung');

    if (wertentwicklung) wertentwicklung.style.display = chartName === 'wertentwicklung' ? 'block' : 'none';
    if (preisentwicklung) preisentwicklung.style.display = chartName === 'preisentwicklung' ? 'block' : 'none';
  }

  showChartWertentwicklung() {
    this.showChart('wertentwicklung');
  }

  showChartPreisentwicklung() {
    this.showChart('preisentwicklung');
  }

  setDateRange() {
    this.fondsDetailChart.updateDateRangeSelectors(this.initialDateRange);
  }

  splitRangeString(rangeValue) {
    const rangeArray = [];
    rangeArray[0] = parseInt(rangeValue, 10);
    rangeArray[1] = rangeValue.replace(/[0-9]/g, '');
    return rangeArray;
  }

  async getChartData() {
    console.log('FondsDetail.getChartData()');
    const rangeArray = this.splitRangeString(this.initialDateRange);
    const start = moment(this.letzteBewertungDate).subtract(rangeArray[0], rangeArray[1].toUpperCase()).unix();
    const end = moment(this.letzteBewertungDate).unix();
    const range = this.selectedDateRange ? this.selectedDateRange : this.initialDateRange;
    const parameter = `&type=initial&oneyear=${this.isFondsYoungerThanOneYear}&range=${range}&start=${start}&end=${end}`;
    const request = `?eID=fonds&function=chart&fondsId=${this.fondsAndiId}${parameter}`;

    try {
      const response = await fetch(request);

      if (!response.ok) throw new Error('Network response was not ok');

      const result = await response.json();

      return result;
    } catch (error) {
      console.error('error', error);
    }
  }

  renderIcmaLegend(chartData) {
    const icmaData = chartData.icma_data;

    if (icmaData) {
      let output = '';
      const outputTarget = document.getElementById('icma_legend');
      const outputContainer = document.getElementById('icma_legend_container');

      if (outputContainer) outputContainer.style.display = 'block';

      Object.entries(icmaData).forEach(([key, item]) => {
        output += `<dt class="icma-legend-label ${key}"><span class="icma-legend-badge ${key}"></span>${item.label}</dt><dd class="icma-legend-text">${item.text}</dd>`;
      });

      if (outputTarget) outputTarget.innerHTML = output;
    }
  }



  /********************************************************************
   * Helpers
   ********************************************************************/
  checkFondsManagerImageAvailable() {
    let fondsManagerName = '';

    try {
      fondsManagerName = this.fondsManagerImage(this.fondsDetailData.fondsmanager);
    } catch (e) {
      console.error(e);
    }

    let request = `${this.settings.fileadminPath}fondsmanager/${fondsManagerName}.jpg`;

    if (AMApp.isLocal) {
      request = `img/dummy/${fondsManagerName}.jpg`;
    }

    try {
      const response = fetch(request, { method: 'HEAD' });
      const imageElement = document.getElementById('fondsmanager_image');

      if (imageElement) {
        if (response.ok) {
          imageElement.style.display = 'block';
        } else {
          imageElement.style.display = 'none';
        }
      }
    } catch (error) {
      const imageElement = document.getElementById('fondsmanager_image');
      if (imageElement) imageElement.style.display = 'none';
    }
  }

  removeEmptyTableCells() {
    document.querySelectorAll('table .data').forEach(cell => {
      if (cell.textContent === '') {
        const row = cell.closest('tr');
        if (row) row.remove();
      }
    });
  }

  /**
   * Show message
   */
  showMessage(target, message) {
    if (target) {
      const messageElement = document.createElement('p');
      messageElement.className = 'fondsdetail-noresult';
      messageElement.textContent = message;
      target.insertAdjacentElement('beforebegin', messageElement);
    }
  }

  /**
   * Remove loader
   */
  removeLoader() {
    document.querySelectorAll('.loader').forEach(loader => loader.remove());
  }

  setGloss(data) {
    let gloss = {
      taxinfo: '3',
      sustainability: '',
      fondsPerformance: '',
      benchmark: '',
      resourceIndex: ''
    };

    switch (data.INSD_EXPORT_TYPE) {
      case 'AKT':
      case 'DACH':
        gloss.verwaltungsverguetung = '1';
        gloss.gesamtkostenquote = '2';
        gloss.kennzahlen = '4';
        gloss.volatilitaet = '5';
        gloss.sharpRatio = '6';
        gloss.fondsdetailMaxLoose = '7';
        gloss.fondsdetailMaxEarn = '7';
        gloss.fondsdetailVar = '8';
        gloss.fondsPerformance = '9';
        break;
      case 'AKTS4':
        gloss.verwaltungsverguetung = '1';
        gloss.gesamtkostenquote = '2';
        gloss.kennzahlen = '4';
        gloss.volatilitaet = '5';
        gloss.sharpRatio = '6';
        gloss.fondsdetailMaxLoose = '7';
        gloss.fondsdetailMaxEarn = '7';
        gloss.fondsdetailVar = '8';
        gloss.sustainability = '9';
        gloss.fondsPerformance = '10';
        break;
      case 'RENTI3':
      case 'MISCHNH':
        gloss.verwaltungsverguetung = '1';
        gloss.gesamtkostenquote = '2';
        gloss.rlzCash = '4';
        gloss.kennzahlen = '5';
        gloss.volatilitaet = '6';
        gloss.sharpRatio = '7';
        gloss.fondsdetailMaxLoose = '8';
        gloss.fondsdetailMaxEarn = '8';
        gloss.fondsdetailVar = '9';
        gloss.sustainability = '10';
        gloss.fondsPerformance = '11';
        break;
      case 'ROH':
        gloss.verwaltungsverguetung = '1';
        gloss.gesamtkostenquote = '2';
        gloss.rlzCash = '4';
        gloss.kennzahlen = '5';
        gloss.volatilitaet = '6';
        gloss.sharpRatio = '7';
        gloss.fondsdetailMaxLoose = '8';
        gloss.fondsdetailMaxEarn = '8';
        gloss.fondsdetailVar = '9';
        gloss.resourceIndex = '10';
        gloss.fondsPerformance = '11';
        break;
      case 'ROHFLEX':
        gloss.verwaltungsverguetung = '1';
        gloss.gesamtkostenquote = '2';
        gloss.rlzCash = '4';
        gloss.kennzahlen = '5';
        gloss.volatilitaet = '6';
        gloss.sharpRatio = '7';
        gloss.fondsdetailMaxLoose = '8';
        gloss.fondsdetailMaxEarn = '8';
        gloss.fondsdetailVar = '9';
        gloss.investitionsstatus = '10';
        gloss.resourceIndex = '11';
        gloss.fondsPerformance = '12';
        break;
      case 'MISCH':
      case 'RENT':
      case 'TREND':
      case 'MISCH-2':
        gloss.verwaltungsverguetung = '1';
        gloss.gesamtkostenquote = '2';
        gloss.rlzCash = '4';
        gloss.kennzahlen = '5';
        gloss.volatilitaet = '6';
        gloss.sharpRatio = '7';
        gloss.fondsdetailMaxLoose = '8';
        gloss.fondsdetailMaxEarn = '8';
        gloss.fondsdetailVar = '9';
        gloss.fondsPerformance = '10';
        break;
    }

    data.gloss = gloss;
    return data;
  }

  fondsManagerImage(value) {
    if (value) {
      const cleaned = value
        .replace(/\//g, '_')
        .replace(/ /g, '_')
        .replace(/ä/g, 'ae')
        .replace(/ü/g, 'ue')
        .replace(/ö/g, 'oe')
        .replace(/ß/g, 'ss')
        .toLowerCase();

      return encodeURI(cleaned);
    }

    return value;
  }

  getFondsCategory(categories) {
    return this.fondsCategories.find(category => categories.includes(category));
  }
}
